// src/hooks/useDeviceLayout.ts
import { useMantineTheme } from "@mantine/core";
import { useMediaQuery, useViewportSize } from "@mantine/hooks";

export enum DeviceLayout {
	PhonePortrait = 0,
	PhoneLandscape = 1,
	TabletPortrait = 2,
	TabletLandscape = 3,
	Desktop = 4,
}

export type DeviceLayoutResult = {
	deviceLayout: DeviceLayout;
	isPhoneOrSmaller: boolean;
	isTabletOrSmaller: boolean;
	isLandscape: boolean;
	isPortrait: boolean;
};

export const useDeviceLayout = (): DeviceLayoutResult => {
	const theme = useMantineTheme();
	const { width, height } = useViewportSize();

	// Basic width-based breakpoints (same as before)
	const widthIsSmOrSmaller = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const heightIsSmOrSmaller = useMediaQuery(`(max-height: ${theme.breakpoints.sm})`);
	const isSmOrSmaller = widthIsSmOrSmaller || heightIsSmOrSmaller;
	
	const widthIsMdOrSmaller = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
	const isMdOrSmaller = widthIsMdOrSmaller;

	// Derive orientation from viewport dimensions
	const isLandscape = width > height;
	const isPortrait = !isLandscape;

	let deviceLayout: DeviceLayout;

	// > md → Desktop
	if (!isMdOrSmaller) {
		deviceLayout = DeviceLayout.Desktop;
	}
	// sm < width <= md → Tablet
	else if (!isSmOrSmaller) {
		deviceLayout = isLandscape
			? DeviceLayout.TabletLandscape
			: DeviceLayout.TabletPortrait;
	}
	// width <= sm → Phone
	else {
		deviceLayout = isLandscape
			? DeviceLayout.PhoneLandscape
			: DeviceLayout.PhonePortrait;
	}

	const isPhoneOrSmaller = deviceLayout <= DeviceLayout.PhoneLandscape;
	const isTabletOrSmaller = deviceLayout <= DeviceLayout.TabletLandscape;

	return {
		deviceLayout,
		isPhoneOrSmaller,
		isTabletOrSmaller,
		isLandscape,
		isPortrait,
	};
};
